import React from 'react';
import { Spinner } from '../Common/Loader';

interface SchemeSummaryCardProps {
  productName?: string;
  eligibleUnits?: string | number;
  freeProducts?: string | number;
  city?: string;
  crateMapping?: string;
  onRemoveCity?: (cityName: string) => void;
  onCancel?: () => void;
  onSubmit?: () => void;
  isSubmitting?: boolean;
  submitButtonText?: string;
}

export const SchemeSummaryCard: React.FC<SchemeSummaryCardProps> = ({
  productName = '',
  eligibleUnits = '10',
  freeProducts = '1',
  city = 'All Cities (Global)',
  crateMapping = '20 Units/Crate',
  onRemoveCity,
  onCancel,
  onSubmit,
  isSubmitting = false,
  submitButtonText = 'Create Scheme',
}) => {
  const units = eligibleUnits || 10;
  const free = freeProducts || 1;
  const prod = productName || '';
  const selectedCity = city || '';
  const displayCities: string[] = selectedCity
    .split(',')
    .map((c: string) => c.trim())
    .filter((c: string) => Boolean(c) && !c.startsWith('Select All'));

  return (
    <div className="bg-white rounded-2xl p-5 md:p-6 border border-gray-100 shadow-sm mt-6">
      {/* SCHEME SUMMARY Header */}
      <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">
        SCHEME SUMMARY
      </div>

      {/* Main Title */}
      <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1">
        Buy 10, Get 1 Free
      </h3>

      {/* Description */}
      <p className="text-xs md:text-sm text-gray-500 mb-2 max-w-3xl">
        Distributors purchasing {units} crates{prod ? ` of ${prod}` : ''} will receive {free} free unit.
      </p>

      {displayCities.length > 0 && (
        <>
          <p className="text-xs md:text-sm text-gray-500 mb-3 max-w-3xl">
            Applicable in selected regions.
          </p>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {displayCities.map((cityName: string, idx: number) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1 text-xs font-semibold border border-red-200 bg-red-50/50 text-[#B71C1C] px-3 py-1.5 rounded-xl transition-all"
              >
                {onRemoveCity && (
                  <button
                    type="button"
                    onClick={() => onRemoveCity(cityName)}
                    className="rounded-full p-0.5 transition-colors cursor-pointer text-[#B71C1C]"
                    title={`Remove ${cityName}`}
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="black"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
                <span>{cityName}</span>
              </span>
            ))}
          </div>
        </>
      )}

      {/* PRODUCT DETAILS Header & Tag */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-t border-gray-100 pt-4">
        <div>
          {prod && (
            <>
              <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                PRODUCT DETAILS
              </div>

              <div className="inline-flex items-center gap-2 border border-red-200 bg-red-50/50 text-[#B71C1C] rounded-lg px-3 py-1.5 text-xs font-semibold">
                {/* Box Icon */}
                <svg className="w-4 h-4 text-[#B71C1C]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                <span>
                  {prod} ({crateMapping})
                </span>
              </div>
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3 self-end">
          <button
            type="button"
            onClick={onCancel}
            className="border border-gray-300 hover:bg-gray-50 text-gray-700 text-xs md:text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors cursor-pointer"
          >
            Cancel Changes
          </button>

          <button
            type="button"
            onClick={onSubmit}
            disabled={isSubmitting}
            className="bg-[#B71C1C] hover:bg-[#960101] text-white text-xs md:text-sm font-semibold px-6 py-2.5 rounded-lg shadow-sm transition-all cursor-pointer flex items-center gap-2 disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <Spinner size="sm" color="text-white" />
                Saving...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                {submitButtonText}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
